import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Pagination,
  Box,
  Button,
  CircularProgress,
  Backdrop,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CandidatoPrerregistrado } from '../../modules/apps/user-management/users-list/core/_models';
import { getCandidatosPrerregistradosEvento } from '../../modules/apps/user-management/users-list/core/_requests';

export const CandidatosPrerregistradosPage: React.FC = () => {
  const params = useParams<{ id?: string; eventoId?: string; idEvento?: string }>();
  const id = params.id || params.eventoId || params.idEvento;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [candidatos, setCandidatos] = useState<CandidatoPrerregistrado[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  useEffect(() => {
  }, [location, id]);

  const fetchCandidatos = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    try {
      const res = await getCandidatosPrerregistradosEvento(id, {
        page,
        limit: 10,
      });

      const payload = res?.data || res;
      const listaCandidatos = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.candidatos)
        ? payload.candidatos
        : [];
      const totalRegistros = payload?.total || payload?.totalRecords || payload?.count;
      const paginasCalculadas = totalRegistros ? Math.ceil(totalRegistros / 10) : 1;
      const totalPaginas =
        payload?.totalPages ||
        payload?.pageCount ||
        res?.totalPages ||
        paginasCalculadas;

      setCandidatos(listaCandidatos);
      setTotalPages(totalPaginas);
    } catch (err: any) {
      console.error(' Error al obtener candidatos prerregistrados:', err);
      setCandidatos([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    fetchCandidatos();
  }, [fetchCandidatos]);

  const candidatosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return candidatos;

    const term = searchTerm.toLowerCase().trim();

    return candidatos.filter((item) => {
      const comp = item.User?.UserComplemento;
      const numDoc = comp?.numero_doc?.toString().toLowerCase() || '';
      const tipoDoc = comp?.tipo_doc?.toLowerCase() || '';
      const nombreCompleto = [
        comp?.primer_nome,
        comp?.segundo_nome,
        comp?.primer_apelido,
        comp?.segundo_apelido,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return (
        numDoc.includes(term) ||
        tipoDoc.includes(term) ||
        nombreCompleto.includes(term)
      );
    });
  }, [candidatos, searchTerm]);

  const getNombreCompleto = (item: CandidatoPrerregistrado): string => {
    const comp = item.User?.UserComplemento;
    if (!comp) return 'N/A';
    return [comp.primer_nome, comp.segundo_nome, comp.primer_apelido, comp.segundo_apelido]
      .filter(Boolean)
      .join(' ');
  };

  const formatFecha = (fechaStr: string): string => {
    if (!fechaStr) return 'N/A';
    const fecha = new Date(fechaStr.includes('T') ? fechaStr : fechaStr.replace(/-/g, '/'));
    return isNaN(fecha.getTime()) ? fechaStr : fecha.toLocaleDateString('es-CO');
  };

  return (
    <div
      className='p-4 shadow-4 rounded-3 mt-15'
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0px 3px 6px #00000029',
      }}
    >
      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>

      {/* Encabezado y navegación */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm='auto'>
            <Button
              variant='contained'
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: '#1976D2',
                '&:hover': { backgroundColor: '#1976D2' },
                textTransform: 'none',
                fontWeight: 'bold',
                px: 3,
                py: 1,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Volver
            </Button>
          </Grid>
          <Grid item xs={12} sm>
            <Typography
              variant='h6'
              component='h1'
              sx={{
                fontWeight: 'bold',
                color: '#00050a',
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
              VER CANDIDATOS PRERREGISTRADOS EVENTO
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ border: 1, borderColor: '#B0B0B0', mb: 3 }} />

      {/* Buscador */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} lg={6}>
            <TextField
              fullWidth
              size='small'
              placeholder='Número de documento / Nombre completo del candidato'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant='outlined'
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#0A4396',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabla con scroll horizontal en pantallas pequeñas */}
      <TableContainer
        component={Paper}
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: '#E0E0E0',
          overflowX: 'auto',
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead style={{ backgroundColor: '#1976D2' }}>
            <TableRow>
              <TableCell style={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                Tipo de documento
              </TableCell>
              <TableCell style={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                Número de documento
              </TableCell>
              <TableCell style={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                Nombre completo del candidato
              </TableCell>
              <TableCell style={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                Fecha prerregistro
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidatosFiltrados.length > 0 ? (
              candidatosFiltrados.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    '&:hover': { backgroundColor: '#F5F8FC' },
                  }}
                >
                  <TableCell>{item.User?.UserComplemento?.tipo_doc || 'N/A'}</TableCell>
                  <TableCell>{item.User?.UserComplemento?.numero_doc || 'N/A'}</TableCell>
                  <TableCell>{getNombreCompleto(item)}</TableCell>
                  <TableCell>{formatFecha(item.fecha_prerregistro)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align='center' sx={{ py: 3 }}>
                  <Typography variant='body2' color='textSecondary'>
                    {loading ? 'Cargando candidatos...' : 'No se encontraron registros.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            shape='rounded'
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#1976D2',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#1976D2',
                },
              },
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default CandidatosPrerregistradosPage;